/* eslint-disable i18next/no-literal-string -- a code-only module: every literal is a storage key, scope, header or result code; it renders nothing */
// The web app's side of the offline runtime (runtime.ts): Supabase, the
// stored session, the owner-derived localStorage the C3 wipe removes, the
// transports. main.tsx starts the runtime with it.
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../supabase'
import { readStoredRefreshSync, readStoredSession, removeLocalSession } from '../localSession'
import { setStoredRefreshReader } from './refreshObserver'
import { broadcastTransition, fencedFetch } from '../accountChannel'
import { apiFetch } from '../../i18n/apiFetch'
import { apiUrl } from '../../api/apiUrl'
import { fetchCardImages } from '../../api/cardImages'
import { parseDecklist, extractCommanders } from '../decklist'
import { readSessionIdentity } from '../accessToken'
import { collectionKeyFor } from '../collectionScope'
import { readCollectionRecord } from '../collectionCache'
import { clearAccountCollectionCache } from '../../hooks/useCollection'
import { clearRetryIntents } from '../copiesRetryIntents'
import { clearPendingPullsForUser } from '../sealedPendingPulls'
import { clearCapabilityForUser } from '../copiesCapability'
import { clearCopyLineEdits } from '../copyLineEdits'
import { forgetAsked } from '../signupEmailPrompt'
import { purgeUserCaches } from '../serviceWorker'
import { draftKeysForScope } from '../../components/decks/editor/editorSaveQueue'
import { textDraftKeysForScope } from '../../components/decks/editor/textDraftStore'
import { tc } from '../../i18n'
import { C3_REASON, FENCE_MIRROR_KEY, type C3Reason } from './offline.codes'
import { ownerSubject } from './identityMachine'
import type { OfflineAuthAdapter } from './runtime'
import { currentTransitionConfirmer } from './transitionConfirm'

/** Account caches (useAuth's USER_LOCAL_STORAGE_KEYS that are not the guest store). */
const ACCOUNT_CACHE_KEYS = ['cb-commander-names', 'cb-commander-names-ts', 'cb_archidekt_username', 'cb-post-auth-dest']

export { registerTransitionConfirmer } from './transitionConfirm'

function defaultConfirm(count: number): Promise<boolean> {
  if (typeof window === 'undefined' || typeof window.confirm !== 'function') return Promise.resolve(true)
  return Promise.resolve(window.confirm(tc('offline.signout_warning', { count })))
}

/** Every key of a Storage whose name or value names the departed account (inv. 4), except the fence mirror. */
function sweepStorage(storage: Storage | undefined, uuid: string): void {
  if (!storage) return
  const doomed: string[] = []
  for (let i = 0; i < storage.length; i++) {
    const k = storage.key(i)
    if (!k || k === FENCE_MIRROR_KEY) continue
    let v: string | null = null
    try { v = storage.getItem(k) } catch { v = null }
    if (k.includes(uuid) || (v && v.includes(uuid))) doomed.push(k)
  }
  for (const k of doomed) storage.removeItem(k)
}

async function wipeExtra(ownerKey: string, reason: C3Reason): Promise<void> {
  const uuid = ownerSubject(ownerKey)
  if (!uuid) return
  // Named stores first (they clear their in-memory copies too).
  for (const k of draftKeysForScope(uuid)) localStorage.removeItem(k)
  for (const k of textDraftKeysForScope(uuid)) localStorage.removeItem(k)
  if (!clearAccountCollectionCache(uuid) && !clearAccountCollectionCache(uuid)) throw new Error('collection cache not cleared')
  clearRetryIntents({ userId: uuid })
  clearPendingPullsForUser(uuid)
  clearCapabilityForUser(uuid)
  // The queued per-copy changes' baselines and pending edits (memory only):
  // gone, and every outstanding callback finds its state dead.
  clearCopyLineEdits()
  forgetAsked(uuid)
  for (const k of ACCOUNT_CACHE_KEYS) localStorage.removeItem(k)
  if (reason === C3_REASON.deleteAccount) {
    for (const k of ['commander-bracket-saved-decks', 'commander-bracket-collection', 'commander-bracket-collection-claimed', 'commander-bracket-collection-pending-sync', 'commander-bracket-migrated']) {
      localStorage.removeItem(k)
    }
  }
  // Anything else that names the account (inv. 4: no key or value may).
  sweepStorage(typeof localStorage !== 'undefined' ? localStorage : undefined, uuid)
  sweepStorage(typeof sessionStorage !== 'undefined' ? sessionStorage : undefined, uuid)
  const purge = await purgeUserCaches()
  if (purge && purge.ok === false) throw new Error('caches not purged')
}

function extraUnsyncedDeckIds(ownerKey: string): string[] {
  const uuid = ownerSubject(ownerKey)
  if (!uuid) return []
  const ids = new Set<string>()
  const prefixes = [`cb_editor_draft:${uuid}:`]
  for (const k of draftKeysForScope(uuid)) {
    const rest = k.slice(prefixes[0].length)
    const id = rest.split(':')[0]
    if (id) ids.add(id)
  }
  for (const k of textDraftKeysForScope(uuid)) {
    const parts = k.split(':')
    // cb_text_draft:<scope>:<deck>:<writer>
    if (parts.length >= 3) ids.add(parts[2])
  }
  return [...ids]
}

export function createAppOfflineAdapter(): OfflineAuthAdapter {
  // Refresh answers bind to the stored token (also registered by supabase.ts
  // at client creation; the same reader).
  setStoredRefreshReader(() => readStoredRefreshSync(supabase))
  return {
    storedSession: () => readStoredSession(supabase),
    localSignOut: async (ownerKey) => {
      const stored = await readStoredSession(supabase)
      const uuid = ownerSubject(ownerKey)
      // The stored session is not the departing owner's (an account switch
      // already holds the incoming one): nothing of the departing owner to remove.
      if (stored === null || (stored !== 'unknown' && stored.subject !== uuid)) return true
      if (!supabase) return true
      try {
        await supabase.auth.signOut({ scope: 'local' })
      } catch {
        // Verified below either way.
      }
      // Only a positively verified removal counts (r2 #12): 'unknown' keeps
      // the tombstone and the device blocked.
      const removal = await removeLocalSession(supabase)
      return removal === 'removed'
    },
    wipeExtra,
    extraUnsyncedDeckIds,
    refreshSession: async () => {
      if (!supabase) return { ok: false, status: 0, code: null, missing: false }
      const stored = await readStoredSession(supabase)
      if (stored === null) return { ok: false, status: 400, code: null, missing: true }
      try {
        const { data, error } = await supabase.auth.refreshSession()
        if (!error && data.session?.user?.id) return { ok: true, subject: data.session.user.id, expiresAt: data.session.expires_at ?? null }
        const e = error as { status?: number; code?: string; name?: string } | null
        return { ok: false, status: e?.status ?? 0, code: e?.code ?? null, missing: e?.name === 'AuthSessionMissingError' }
      } catch {
        return { ok: false, status: 0, code: null, missing: false }
      }
    },
    sessionIdentity: async () => {
      const s = await readSessionIdentity()
      return s.ok ? { userId: s.userId, token: s.token } : null
    },
    supabaseRest: () => (SUPABASE_URL && SUPABASE_ANON_KEY ? { url: SUPABASE_URL, anonKey: SUPABASE_ANON_KEY } : null),
    supabaseFetch: fencedFetch,
    apiFetch: (url, init) => apiFetch(url, init),
    apiUrl,
    collectionIdFor: (userId) => readCollectionRecord(collectionKeyFor(userId)).collectionId,
    fetchCardInfo: async (names) => (await fetchCardImages(names)).found,
    cardNamesOf: (decklist) => [...new Set([...parseDecklist(decklist).map(c => c.name), ...extractCommanders(decklist)])],
    broadcast: (msg) => broadcastTransition(msg as Parameters<typeof broadcastTransition>[0]),
    reload: () => { if (typeof window !== 'undefined') window.location.reload() },
    // The app shell's modal (TransitionConfirmHost); without one, confirm().
    confirm: (count, reason) => {
      const c = currentTransitionConfirmer()
      return c ? c(count, reason) : defaultConfirm(count)
    },
  }
}
